import { FC, useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import { getAxiosWithAuthorization, useDeepCompareMemo } from '@clatter/platform';
import { config } from '../../config';
import { getLocalStorageAccessToken } from '../../helpers/auth';
import {
  useGetTypesByCmsScopeQuery,
  useUpdateEntityMutation,
  useDeleteEntityMutation,
  useAddEntityToCmsScopeMutation,
  useAddEntityMutation,
  useGetTypesQuery,
  useGetAllProjectsQuery,
} from '../../services';
import { ConfirmationDialog, NgCard, NgCardContent, SkeletonLoader } from '../../components';
import { useOutletContext } from 'react-router-dom';
import { buttonVariants, IconButton } from '@clatter/ui';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Stack from '@mui/material/Stack';
import { useDeleteCmsScopeTypeMutation } from '../../services/ccms.service';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store';

interface ProjectData {
  ID: number;
  DisplayName: string;
  CmsScopeID: number;
  ProjectTemplateID?: number;
  ProjectTemplate?: {
    ID?: number;
    CmsScopeID: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
  CmsScope?: {
    ID: number;
  };
}

interface EntityData {
  ID: number;
  [key: string]: unknown;
}

interface TypeData {
  id: number;
  name: string;
  displayName?: string;
  count?: number;
  cmsScopeId?: number;
}

interface SchemaField {
  type: string;
  label: string;
  order: number;
}

interface EntitiesPageProps {
  projectData?: ProjectData;
  isLoading?: boolean;
  isGlobal?: boolean;
}

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  height: 70vh;
  gap: 16px;
`;

const StyledLeftPanel = styled.div`
  border-right: 1px solid #e0e0e0;
  padding-right: 16px;
`;

const StyledRightPanel = styled.div`
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);
`;

const StyledTypesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledTypeItem = styled.div<{ isSelected: boolean }>`
  padding: 12px 16px;
  border-radius: 6px;
  cursor: pointer;
  background: ${(props) => (props.isSelected ? '#007bff' : '#f8f9fa')};
  color: ${(props) => (props.isSelected ? 'white' : '#333')};
  border: 1px solid ${(props) => (props.isSelected ? '#007bff' : '#e0e0e0')};
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.isSelected ? '#0056b3' : '#e9ecef')};
  }
`;

const StyledTypeName = styled.div`
  font-weight: 500;
  font-size: 0.95rem;
`;

const StyledTypeCount = styled.div`
  font-size: 0.8rem;
  opacity: 0.8;
  margin-top: 2px;
`;

const StyledTableContainer = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  position: relative;
  max-width: 100%;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  font-size: 0.9rem;
  width: 100%;
  table-layout: fixed;
`;

const StyledTableHeader = styled.thead`
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const StyledTableHeaderCell = styled.th<{ width?: string; minWidth?: string; isFixed?: boolean }>`
  padding: 12px 8px;
  text-align: left;
  border-bottom: 2px solid #e0e0e0;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  width: ${(props) => props.width || 'auto'};
  min-width: ${(props) => props.minWidth || '100px'};
  overflow: hidden;
  text-overflow: ellipsis;

  ${(props) =>
    props.isFixed &&
    `
    position: sticky;
    right: 0;
    background: #f8f9fa;
    z-index: 3;
    border-left: 2px solid #e0e0e0;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
  `}
`;

const StyledTableBody = styled.tbody``;

const StyledTableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const StyledTableCell = styled.td<{ width?: string; minWidth?: string; isFixed?: boolean }>`
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: top;
  width: ${(props) => props.width || 'auto'};
  min-width: ${(props) => props.minWidth || '100px'};
  // overflow: hidden;
  // text-overflow: ellipsis;
  white-space: nowrap;

  ${(props) =>
    props.isFixed &&
    `
    position: sticky;
    right: 0;
    background: white;
    border-left: 2px solid #e0e0e0;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
  `}

  tr:hover & {
    background: #f8f9fa;
  }

  ${(props) =>
    props.isFixed &&
    `
    tr:hover & {
      background: #f8f9fa;
    }
  `}
`;

const StyledEditableCell = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  padding: 4px;
  font-size: 0.9rem;
  min-width: 0;
  // overflow: hidden;
  // text-overflow: ellipsis;

  &:focus {
    outline: 2px solid #007bff;
    background: white;
    border-radius: 3px;
    overflow: visible;
  }
`;

const StyledActionsCell = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledActionButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #007bff;
  background: transparent;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background: #007bff;
    color: white;
  }
`;

const StyledEmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #666;
  font-style: italic;
`;

const StyledTableTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
`;

const StyledTableTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const StyledModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const StyledModal = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const StyledModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const StyledModalTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
`;

const StyledCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;

  &:hover {
    color: #333;
  }
`;

const StyledModalContent = styled.div`
  margin-bottom: 20px;
`;

const StyledFormField = styled.div`
  margin-bottom: 16px;
`;

const StyledLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  color: #333;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const StyledModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const StyledButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }

  ${(props) => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #007bff;
          color: white;
          border-color: #007bff;

          &:hover:not(:disabled) {
            background: #0056b3;
            border-color: #0056b3;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          border-color: #dc3545;

          &:hover:not(:disabled) {
            background: #c82333;
            border-color: #c82333;
          }
        `;
      default:
        return `
          background: white;
          color: #333;
          border-color: #e0e0e0;

          &:hover:not(:disabled) {
            background: #f8f9fa;
          }
        `;
    }
  }}
`;

const jsTypeToInputTypeMap: Record<string, React.HTMLInputTypeAttribute> = {
  string: 'text',
  number: 'number',
  boolean: 'checkbox',
  bool: 'checkbox',
  date: 'date',
};

const EntitiesPage: FC<EntitiesPageProps> = ({
  projectData,
  isLoading: isParentLoading,
  isGlobal,
}) => {
  const ctx = useOutletContext<{ reload: number } | undefined>();
  const { reload } = ctx || {};

  const isProject = !!(projectData?.ProjectTemplateID || projectData?.ProjectTemplate);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [entitiesData, setEntitiesData] = useState<Record<string, EntityData[]>>({});
  const [loadingEntities, setLoadingEntities] = useState<Set<string>>(new Set());
  const [editableData, setEditableData] = useState<Record<string, Record<string, any>>>({});
  const [editingEntity, setEditingEntity] = useState<EntityData | null>(null);
  const [deletingEntity, setDeletingEntity] = useState<EntityData | null>(null);
  const [modalEditData, setModalEditData] = useState<Record<string, any>>({});
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [deletingType, setDeletingType] = useState<TypeData | null>(null);
  const [createData, setCreateData] = useState<Record<string, any>>({});
  const dispatch = useDispatch();
  const [deleteCmsScopeType, { isLoading: isDeleteCmsScopeTypeLoading }] = useDeleteCmsScopeTypeMutation();
  const templateId = !isProject && !isGlobal ? projectData?.ID : undefined;
  const { data: associatedProjects = [] } = useGetAllProjectsQuery(templateId ? { templateId } : undefined, {
    skip: !templateId,
  });

  // Fetch types using CMS scope endpoint
  const {
    data: typesData,
    isLoading: isTypesLoading,
    isUninitialized: isTypesQueryUninitialized,
    refetch,
  } = isGlobal
    ? useGetTypesQuery(null, { skip: false })
    : useGetTypesByCmsScopeQuery(
        { cmsScopeId: projectData?.CmsScopeID || projectData?.CmsScope?.ID || 0 },
        { skip: !projectData?.CmsScopeID && !projectData?.CmsScope?.ID },
      );

  // Mutation hooks for update, delete, and create
  const [updateEntity, { isLoading: isUpdating }] = useUpdateEntityMutation();
  const [deleteEntity, { isLoading: isDeleting }] = useDeleteEntityMutation();
  const [addEntity, { isLoading: isCreatingEntity }] = isGlobal
    ? useAddEntityMutation()
    : useAddEntityToCmsScopeMutation();

  const isLoading = isParentLoading || isTypesLoading;

  // Types are already filtered by CMS scope from the endpoint
  const filteredTypesData = useDeepCompareMemo(() => typesData || [], [typesData]);
  const selectedTypeData = useMemo(
    () => filteredTypesData.find((type: TypeData) => type.name === selectedType) || null,
    [filteredTypesData, selectedType],
  );

  // GraphQL URL - use project template ID as workspaceId
  const graphqlUrl = useMemo(() => {
    if (isGlobal) {
      return `${config.assetServiceBaseUrl}/entities/:type`;
    }

    return projectData
      ? `${config.assetServiceBaseUrl}/v2/${isProject ? 'projects' : 'project-templates'}/${projectData.ID}/graphql`
      : null;
  }, [projectData?.ID, isGlobal]);

  // Build GraphQL query from type schema
  const buildGraphQLQuery = (typeName: string): string => {
    // Get schema fields if available
    let fields = 'ID';

    if (selectedTypeData?.schema) {
      const schemaFields = Object.entries(selectedTypeData.schema as Record<string, SchemaField>)
        .map(([key, field]) => ({
          key,
          order: field.order || 0,
        }))
        .sort((a, b) => a.order - b.order)
        .map((f) => f.key);

      if (schemaFields.length > 0) {
        // Include basic fields plus schema fields
        fields = `ID\n${schemaFields.join('\n')}`;
      }
    }

    return `
      query q {
        ${typeName} {
          ${fields}
        }
      }
    `;
  };

  const loadEntitiesForType = async (typeName: string) => {
    if (typeName !== selectedType) {
      return; // Type is changing, ignore this call
    }

    if (!graphqlUrl) {
      console.error('GraphQL URL not available');
      return;
    }

    setLoadingEntities((prev) => new Set([...prev, typeName]));

    try {
      let graphqlResponse: any;

      const headers = {
        Authorization: `Bearer ${getLocalStorageAccessToken()}`,
        'Content-Type': 'application/json',
      };

      if (!isGlobal) {
        const graphqlQuery = buildGraphQLQuery(typeName);

        graphqlResponse = await getAxiosWithAuthorization({
          url: graphqlUrl,
          method: 'POST',
          headers,
          data: { query: graphqlQuery },
        });
      } else {
        graphqlResponse = await getAxiosWithAuthorization({
          url: graphqlUrl.replace(':type', typeName),
          method: 'GET',
          headers,
        });
      }

      let entities: EntityData[] = [];

      if (graphqlResponse?.data?.[typeName]) {
        entities = graphqlResponse.data[typeName];
      } else if (isGlobal && graphqlResponse?.data?.length) {
        entities = graphqlResponse.data.map((itemInResponse: { ID: number; Data: any }) => ({
          ID: itemInResponse.ID,
          ...itemInResponse.Data,
        }));
      }

      setEntitiesData((prev) => ({
        ...prev,
        [typeName]: entities,
      }));

      // Initialize editable data
      const editableDataForType: Record<string, any> = {};
      entities.forEach((entity: EntityData) => {
        editableDataForType[entity.ID] = { ...entity };
      });

      setEditableData((prev) => ({
        ...prev,
        ...editableDataForType,
      }));
    } catch (error) {
      console.error(`Failed to load entities for type ${typeName}:`, error);
      setEntitiesData((prev) => ({
        ...prev,
        [typeName]: [],
      }));
    } finally {
      setLoadingEntities((prev) => {
        const newSet = new Set(prev);
        newSet.delete(typeName);
        return newSet;
      });
    }
  };

  // Load entities once type schema is loaded
  useEffect(() => {
    if (!selectedType) {
      return;
    }

    // Function to load entities for a type using GraphQL
    loadEntitiesForType(selectedType);
  }, [selectedType, selectedTypeData, reload]);

  useEffect(() => {
    if (isTypesQueryUninitialized) {
      return;
    }

    refetch();
  }, [reload, isTypesQueryUninitialized, refetch]);

  const handleTypeClick = (typeName: string) => {
    setSelectedType(typeName);
    // Don't load entities immediately - wait for type schema to load first
  };

  const handleCellEdit = (entityId: number, field: string, value: string) => {
    setEditableData((prev) => ({
      ...prev,
      [entityId]: {
        ...prev[entityId],
        [field]: value,
      },
    }));
  };

  const handleEditEntity = (entity: EntityData) => {
    setEditingEntity(entity);
    // Extract all fields except ID for editing
    const { ID, ...entityFields } = entity;
    setModalEditData({ ...entityFields });
  };

  const handleDeleteEntity = (entity: EntityData) => {
    setDeletingEntity(entity);
  };

  const handleModalFieldChange = (field: string, value: string, type: string, checked?: boolean) => {
    let transformedValue = transformValue(value, type, checked);

    setModalEditData((prev) => ({
      ...prev,
      [field]: transformedValue,
    }));
  };

  const handleSaveEntity = async () => {
    if (!editingEntity || !selectedType) return;

    try {
      await updateEntity({
        entityType: selectedType,
        entityId: editingEntity.ID,
        data: modalEditData,
      }).unwrap();

      // Update local state
      setEntitiesData((prev) => ({
        ...prev,
        [selectedType]:
          prev[selectedType]?.map((entity) =>
            entity.ID === editingEntity.ID ? { ...entity, ...modalEditData } : entity,
          ) || [],
      }));

      setEditableData((prev) => ({
        ...prev,
        [editingEntity.ID]: { ...prev[editingEntity.ID], ...modalEditData },
      }));

      setEditingEntity(null);
      setModalEditData({});
    } catch (error) {
      console.error('Failed to update entity:', error);
      // TODO: Show error message to user
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingEntity || !selectedType) return;

    try {
      await deleteEntity({
        entityType: selectedType,
        entityId: deletingEntity.ID,
      }).unwrap();

      // Update local state
      setEntitiesData((prev) => ({
        ...prev,
        [selectedType]: prev[selectedType]?.filter((entity) => entity.ID !== deletingEntity.ID) || [],
      }));

      // Remove from editable data
      setEditableData((prev) => {
        const newData = { ...prev };
        delete newData[deletingEntity.ID];
        return newData;
      });

      setDeletingEntity(null);
    } catch (error) {
      console.error('Failed to delete entity:', error);
      // TODO: Show error message to user
    }
  };

  const handleCloseModal = () => {
    setEditingEntity(null);
    setModalEditData({});
  };

  const handleCloseDeleteDialog = () => {
    setDeletingEntity(null);
  };

  const handleCloseDeleteTypeDialog = () => {
    setDeletingType(null);
  };

  const handleDeleteType = (type: TypeData) => {
    setDeletingType(type);
  };

  const handleConfirmDeleteType = async () => {
    if (!deletingType) return;

    if (!projectData?.CmsScopeID) {
      return;
    }

    try {
      await deleteCmsScopeType({
        cmsScopeId: projectData.CmsScopeID,
        typeId: deletingType.id,
      }).unwrap();

      dispatch(
        addNotification({
          type: 'success',
          message: 'Type has been successfully removed.',
        }),
      );

      // Update local state
      setEntitiesData((prev) => {
        delete prev[deletingType.name];
        return prev;
      });

      // Remove from editable data
      setEditableData((prev) => {
        delete prev[deletingType.name];
        return prev;
      });

      if (selectedType && selectedType === deletingType.name) {
        setSelectedType(null);
      }
    } catch (error) {
      console.error('Failed to delete type:', error);
      if (error && typeof error === 'object' && 'status' in error && error.status === 409) {
        dispatch(
          addNotification({
            type: 'error',
            message: 'Failed to remove the type. Existing workspaces depend on it, please remove them first.',
          }),
        );
        return;
      }

      dispatch(
        addNotification({
          type: 'error',
          message: 'Failed to remove the type.',
        }),
      );
    } finally {
      setDeletingType(null);
    }
  };

  const handleCreateEntity = () => {
    setIsCreating(true);
    setCreateData({});
  };

  const transformValue = useCallback((value: string, type: string, checked?: boolean): any => {
    let transformedValue: any = value;

    if (type === 'number') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        transformedValue = numericValue;
      } else {
        transformedValue = 0; // or handle invalid number input as needed
      }
    }

    if (type === 'boolean' || type === 'bool') {
      transformedValue = checked || false;
    }

    return transformedValue;
  }, []);

  const handleCreateFieldChange = (field: string, value: string, type: string, checked?: boolean) => {
    const transformedValue = transformValue(value, type, checked);

    setCreateData((prev) => ({
      ...prev,
      [field]: transformedValue,
    }));
  };

  const handleSaveNewEntity = async () => {
    const cmsScopeId = projectData?.CmsScopeID || projectData?.CmsScope?.ID;

    if (!selectedType || (!cmsScopeId && !isGlobal)) return;

    try {
      await addEntity({
        cmsScopeId,
        entityType: selectedType,
        data: createData,
      }).unwrap();

      // Refresh entities data for the current type
      setEntitiesData((prev) => {
        const newData = { ...prev };
        delete newData[selectedType]; // Remove cached data to force reload
        return newData;
      });

      // Reload entities for the current type
      if (selectedType) {
        loadEntitiesForType(selectedType);
      }

      setIsCreating(false);
      setCreateData({});
    } catch (error) {
      console.error('Failed to create entity:', error);
      // TODO: Show error message to user
    }
  };

  const handleCloseCreateModal = () => {
    setIsCreating(false);
    setCreateData({});
  };

  // Get schema fields from type schema, sorted by order
  const getSchemaFields = (): { key: string; label: string; type: string; order: number }[] => {
    if (!selectedTypeData?.schema) {
      return [];
    }

    const fields = Object.entries(selectedTypeData.schema as Record<string, SchemaField>)
      .map(([key, field]) => ({
        key,
        label: field.label || key,
        type: field.type || 'string',
        order: field.order || 0,
      }))
      .sort((a, b) => a.order - b.order);

    return fields;
  };

  // Get all unique data fields from entities of selected type (excluding ID and PRIMARY_KEY)
  // Fallback to this method if schema is not available
  const getDataFields = (entities: EntityData[]): string[] => {
    const fieldsSet = new Set<string>();
    entities.forEach((entity) => {
      Object.keys(entity).forEach((key) => {
        if (key !== 'ID' && key !== 'PRIMARY_KEY') {
          fieldsSet.add(key);
        }
      });
    });
    return Array.from(fieldsSet).sort();
  };

  const renderEntitiesTable = () => {
    if (!selectedTypeData || !selectedType || !entitiesData[selectedType]) {
      return <StyledEmptyState>Select a type from the left panel to view entities</StyledEmptyState>;
    }

    const entities = entitiesData[selectedType];

    // Use schema fields if available, otherwise fall back to data fields
    const schemaFields = getSchemaFields();
    const dataFields = schemaFields.length > 0 ? schemaFields.map((f) => f.key) : getDataFields(entities);

    // Create field labels mapping
    const fieldLabels: Record<string, string> = {};
    if (schemaFields.length > 0) {
      schemaFields.forEach((f) => {
        fieldLabels[f.key] = f.label;
      });
    } else {
      dataFields.forEach((f) => {
        fieldLabels[f] = f;
      });
    }

    if (entities.length === 0) {
      return (
        <div>
          <StyledEmptyState>No entities found for type "{selectedTypeData.name}"</StyledEmptyState>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <StyledButton variant="primary" onClick={handleCreateEntity}>
              Create New Entity
            </StyledButton>
          </div>
        </div>
      );
    }

    return (
      <StyledTableContainer>
        <StyledTable>
          <StyledTableHeader>
            <tr>
              <StyledTableHeaderCell width="80px" minWidth="80px">
                ID
              </StyledTableHeaderCell>
              {dataFields.map((field) => (
                <StyledTableHeaderCell key={field} width="150px" minWidth="120px">
                  {fieldLabels[field] || field}
                </StyledTableHeaderCell>
              ))}
              <StyledTableHeaderCell width="120px" minWidth="120px" isFixed>
                Actions
              </StyledTableHeaderCell>
            </tr>
          </StyledTableHeader>
          <StyledTableBody>
            {entities.map((entity) => (
              <StyledTableRow key={entity.ID}>
                <StyledTableCell width="80px" minWidth="80px">
                  {entity.ID}
                </StyledTableCell>
                {dataFields.map((field) => (
                  <StyledTableCell key={field} width="150px" minWidth="120px">
                    <StyledEditableCell
                      type="text"
                      value={editableData[entity.ID]?.[field] || ''}
                      onChange={(e) => handleCellEdit(entity.ID, field, e.target.value)}
                      placeholder={entity[field] ? String(entity[field]) : ''}
                      title={entity[field] ? String(entity[field]) : ''}
                    />
                  </StyledTableCell>
                ))}
                <StyledTableCell width="120px" minWidth="120px" isFixed>
                  <StyledActionsCell>
                    <StyledActionButton onClick={() => handleEditEntity(entity)}>Edit</StyledActionButton>
                    <StyledActionButton onClick={() => handleDeleteEntity(entity)}>Delete</StyledActionButton>
                  </StyledActionsCell>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </StyledTableBody>
        </StyledTable>
      </StyledTableContainer>
    );
  };

  return (
    <>
      <NgCard>
        <NgCardContent>
          <StyledContainer>
            {/* Left Panel - Types List */}
            <StyledLeftPanel>
              <h3>Entity Types</h3>
              {isLoading ? (
                <SkeletonLoader count={3} single />
              ) : (
                <>
                  {!filteredTypesData || filteredTypesData.length === 0 ? (
                    <StyledEmptyState>
                      {isGlobal
                        ? 'No entity types found.'
                        : isProject
                        ? 'No entity types found for this workspace.'
                        : 'No entity types found for this workspace template.'}
                    </StyledEmptyState>
                  ) : (
                    <StyledTypesList>
                      {filteredTypesData.map((type: TypeData) => (
                        <StyledTypeItem
                          key={type.id || type.name}
                          isSelected={selectedType === type.name}
                          onClick={() => handleTypeClick(type.name)}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                            <StyledTypeName>{type.displayName || type.name}</StyledTypeName>

                            {!isProject && (
                              /*@ts-ignore*/
                              <IconButton
                                data-id={type.id}
                                onClick={() => handleDeleteType(type)}
                                disabled={associatedProjects.length > 0}
                                tooltip={
                                  associatedProjects.length > 0
                                    ? 'Type cannot be removed when there are workspaces related to the template'
                                    : 'Remove Type'
                                }
                              >
                                <DeleteOutlinedIcon />
                              </IconButton>
                            )}
                          </Stack>

                          {type.count !== undefined && <StyledTypeCount>{type.count} entities</StyledTypeCount>}
                          {loadingEntities.has(type.name) && <StyledTypeCount>Loading...</StyledTypeCount>}
                        </StyledTypeItem>
                      ))}
                    </StyledTypesList>
                  )}
                </>
              )}
            </StyledLeftPanel>

            {/* Right Panel - Entities Table */}
            <StyledRightPanel>
              {selectedTypeData && selectedType && (
                <StyledTableTitleRow>
                  <StyledTableTitle style={{ margin: 0 }}>
                    {selectedTypeData.displayName || selectedTypeData.name} Entities
                  </StyledTableTitle>
                  <StyledButton variant="primary" onClick={handleCreateEntity}>
                    Create New Entity
                  </StyledButton>
                </StyledTableTitleRow>
              )}
              {renderEntitiesTable()}
            </StyledRightPanel>
          </StyledContainer>
        </NgCardContent>
      </NgCard>

      {/* Edit Modal */}
      {editingEntity && (
        <StyledModalOverlay onClick={handleCloseModal}>
          <StyledModal onClick={(e) => e.stopPropagation()}>
            <StyledModalHeader>
              <StyledModalTitle>Edit Entity #{editingEntity.ID}</StyledModalTitle>
              <StyledCloseButton onClick={handleCloseModal}>&times;</StyledCloseButton>
            </StyledModalHeader>
            <StyledModalContent>
              {(() => {
                const schemaFields = getSchemaFields();
                const fieldsToShow =
                  schemaFields.length > 0
                    ? schemaFields.map((f) => ({ key: f.key, label: f.label, type: f.type }))
                    : getDataFields([editingEntity]).map((f) => ({ key: f, label: f, type: typeof editingEntity[f] }));

                return fieldsToShow.map(({ key, label, type }) => (
                  <StyledFormField key={key}>
                    <StyledLabel>{label}</StyledLabel>
                    <StyledInput
                      type={jsTypeToInputTypeMap[type] || 'text'}
                      value={modalEditData[key] ?? ''}
                      onChange={(e) => handleModalFieldChange(key, e.target.value, type, e.target.checked)}
                      placeholder={editingEntity[key] ? String(editingEntity[key]) : ''}
                      checked={type === 'boolean' || type === 'bool' ? Boolean(modalEditData[key]) : undefined}
                    />
                  </StyledFormField>
                ));
              })()}
            </StyledModalContent>
            <StyledModalActions>
              <StyledButton onClick={handleCloseModal} disabled={isUpdating}>
                Cancel
              </StyledButton>
              <StyledButton variant="primary" onClick={handleSaveEntity} disabled={isUpdating}>
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </StyledButton>
            </StyledModalActions>
          </StyledModal>
        </StyledModalOverlay>
      )}

      {/* Delete Entity Confirmation Dialog */}
      {deletingEntity && (
        <StyledModalOverlay onClick={handleCloseDeleteDialog}>
          <StyledModal onClick={(e) => e.stopPropagation()}>
            <StyledModalHeader>
              <StyledModalTitle>Confirm Delete</StyledModalTitle>
              <StyledCloseButton onClick={handleCloseDeleteDialog}>&times;</StyledCloseButton>
            </StyledModalHeader>
            <StyledModalContent>
              <p>Are you sure you want to delete Entity #{deletingEntity.ID}?</p>
              <p>This action cannot be undone.</p>
            </StyledModalContent>
            <StyledModalActions>
              <StyledButton onClick={handleCloseDeleteDialog} disabled={isDeleting}>
                Cancel
              </StyledButton>
              <StyledButton variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete'}
              </StyledButton>
            </StyledModalActions>
          </StyledModal>
        </StyledModalOverlay>
      )}

      {/* Delete Type Confirmation Dialog */}
      <ConfirmationDialog
        dialogState={{
          opened: Boolean(deletingType),
          title: 'Confirm Delete',
          content: (
            <StyledModalContent>
              <h3>
                Are you sure you want to delete type <strong>{deletingType?.displayName}</strong> and it's associated
                entities?
              </h3>
              <p>This action cannot be undone.</p>
            </StyledModalContent>
          ),
          buttons: [
            {
              label: 'Cancel',
              type: 'button',
              variant: buttonVariants.clear,
              callback: handleCloseDeleteTypeDialog,
            },
            {
              label: 'Delete',
              type: 'submit',
              variant: buttonVariants.danger,
              callback: handleConfirmDeleteType,
            },
          ],
        }}
        onDismiss={handleCloseDeleteTypeDialog}
        isLoading={isDeleteCmsScopeTypeLoading}
      />

      {/* Create Modal */}
      {isCreating && (
        <StyledModalOverlay onClick={handleCloseCreateModal}>
          <StyledModal onClick={(e) => e.stopPropagation()}>
            <StyledModalHeader>
              <StyledModalTitle>Create New {selectedTypeData?.name} Entity</StyledModalTitle>
              <StyledCloseButton onClick={handleCloseCreateModal}>&times;</StyledCloseButton>
            </StyledModalHeader>
            <StyledModalContent>
              {(() => {
                const schemaFields = getSchemaFields();
                const fieldsToShow =
                  schemaFields.length > 0
                    ? schemaFields.map((f) => ({ key: f.key, label: f.label, type: f.type }))
                    : []; // If no schema, we can't create entities

                if (fieldsToShow.length === 0) {
                  return <p>No schema available for this entity type. Cannot create new entities.</p>;
                }

                return fieldsToShow.map(({ key, label, type }) => (
                  <StyledFormField key={key}>
                    <StyledLabel>{label}</StyledLabel>
                    <StyledInput
                      type={jsTypeToInputTypeMap[type] || 'text'}
                      value={createData[key] ?? ''}
                      onChange={(e) => handleCreateFieldChange(key, e.target.value, type, e.target.checked)}
                      placeholder={`Enter ${label.toLowerCase()}`}
                      checked={type === 'boolean' || type === 'bool' ? Boolean(createData[key]) : undefined}
                    />
                  </StyledFormField>
                ));
              })()}
            </StyledModalContent>
            <StyledModalActions>
              <StyledButton onClick={handleCloseCreateModal} disabled={isCreatingEntity}>
                Cancel
              </StyledButton>
              <StyledButton variant="primary" onClick={handleSaveNewEntity} disabled={isCreatingEntity}>
                {isCreatingEntity ? 'Creating...' : 'Create Entity'}
              </StyledButton>
            </StyledModalActions>
          </StyledModal>
        </StyledModalOverlay>
      )}
    </>
  );
};

export default EntitiesPage;
